import { useCallback, useEffect, useRef, useState } from 'react'

import {
  catalogVersionReasonLabel,
  listBuyerPrivateSupplierCatalogVersions,
  listSupplierCatalogVersions,
  restoreCatalogVersion,
  type CatalogVersionSummaryDto,
} from '../api/catalogVersions'

export type CatalogVersionHistoryPanelProps = {
  organizationId: number
  mode: 'supplier' | 'buyer-private-supplier'
  privateSupplierId?: number | null
  onRestored?: () => void
}

const formatVersionDate = (value: string): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value || '—'
  }
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const CatalogVersionHistoryPanel = ({
  organizationId,
  mode,
  privateSupplierId = null,
  onRestored,
}: CatalogVersionHistoryPanelProps) => {
  const [open, setOpen] = useState(false)
  const [versions, setVersions] = useState<CatalogVersionSummaryDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingRestore, setPendingRestore] = useState<CatalogVersionSummaryDto | null>(null)
  const [restoring, setRestoring] = useState(false)
  const [restoreMessage, setRestoreMessage] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const canLoadBuyer = mode === 'buyer-private-supplier' && privateSupplierId != null && privateSupplierId > 0

  const loadVersions = useCallback(async () => {
    if (mode === 'buyer-private-supplier' && !canLoadBuyer) {
      setVersions([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const rows =
        mode === 'supplier'
          ? await listSupplierCatalogVersions(organizationId)
          : await listBuyerPrivateSupplierCatalogVersions(organizationId, privateSupplierId as number)
      setVersions(rows)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить версии')
      setVersions([])
    } finally {
      setLoading(false)
    }
  }, [canLoadBuyer, mode, organizationId, privateSupplierId])

  useEffect(() => {
    if (!open) {
      return
    }
    void loadVersions()
  }, [open, loadVersions])

  useEffect(() => {
    if (!pendingRestore) {
      return
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !restoring) {
        setPendingRestore(null)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    dialogRef.current?.focus()
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [pendingRestore, restoring])

  const handleConfirmRestore = async () => {
    if (!pendingRestore) {
      return
    }
    setRestoring(true)
    setError(null)
    try {
      const result = await restoreCatalogVersion(pendingRestore.id)
      setRestoreMessage(
        `Откат выполнен: восстановлено ${result.restored_item_count} поз., деактивировано ${result.deactivated_item_count}. Текущее состояние сохранено как версия #${result.safety_version_id}.`,
      )
      setPendingRestore(null)
      await loadVersions()
      onRestored?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось откатить каталог')
    } finally {
      setRestoring(false)
    }
  }

  return (
    <div className="market-catalog-versions">
      <button
        type="button"
        className="echat-btn echat-btn--ghost"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        {open ? 'Скрыть версии' : 'История версий'}
      </button>

      {open ? (
        <article className="echat-panel market-catalog-versions__panel" aria-label="История версий каталога">
          <header className="market-catalog-versions__header">
            <h2 className="echat-panel__subtitle">Версии каталога</h2>
            <p className="echat-muted">
              Снимки перед импортом и опасными действиями. Откат доступен только оператору.
            </p>
          </header>

          {mode === 'buyer-private-supplier' && !canLoadBuyer ? (
            <p className="market-hint">Выберите поставщика в фильтре, чтобы увидеть версии его прайса.</p>
          ) : null}

          {loading ? <p className="echat-muted">Загрузка…</p> : null}
          {error ? <p className="echat-error">{error}</p> : null}
          {restoreMessage ? <p className="market-hint market-hint--ok">{restoreMessage}</p> : null}

          {!loading &&
          !error &&
          versions.length === 0 &&
          (mode === 'supplier' || canLoadBuyer) ? (
            <p className="echat-muted">Версий пока нет — появятся после следующего импорта.</p>
          ) : null}

          {versions.length > 0 ? (
            <div className="market-table-wrap">
              <table className="market-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Событие</th>
                    <th>Файл</th>
                    <th>Позиций</th>
                    <th>Активных</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {versions.map((version) => (
                    <tr key={version.id}>
                      <td data-label="Дата">{formatVersionDate(version.created_at)}</td>
                      <td data-label="Событие">{catalogVersionReasonLabel(version.reason)}</td>
                      <td data-label="Файл">{version.source_filename || '—'}</td>
                      <td data-label="Позиций">{version.item_count}</td>
                      <td data-label="Активных">{version.active_item_count}</td>
                      <td data-label="Действие">
                        <button
                          type="button"
                          className="echat-btn echat-btn--ghost echat-btn--sm"
                          onClick={() => {
                            setRestoreMessage(null)
                            setPendingRestore(version)
                          }}
                        >
                          Откатить
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </article>
      ) : null}

      {pendingRestore ? (
        <div
          className="market-paywall-overlay"
          role="presentation"
          onClick={() => {
            if (!restoring) {
              setPendingRestore(null)
            }
          }}
        >
          <div
            ref={dialogRef}
            className="market-paywall-modal market-catalog-restore-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="catalog-restore-title"
            aria-describedby="catalog-restore-message"
            tabIndex={-1}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="catalog-restore-title" className="market-paywall-modal__title">
              Откатить каталог к версии?
            </h2>
            <p id="catalog-restore-message" className="market-paywall-modal__message">
              Будет восстановлено состояние на{' '}
              <strong>{formatVersionDate(pendingRestore.created_at)}</strong>
              {pendingRestore.source_filename ? (
                <>
                  {' '}
                  (файл «{pendingRestore.source_filename}»)
                </>
              ) : null}
              . Все текущие изменения после этой даты в выбранном каталоге будут потеряны. Перед откатом
              автоматически сохранится снимок текущего состояния.
            </p>
            <ul className="market-catalog-restore-modal__facts">
              <li>Позиций в версии: {pendingRestore.item_count}</li>
              <li>Активных: {pendingRestore.active_item_count}</li>
              <li>Событие: {catalogVersionReasonLabel(pendingRestore.reason)}</li>
            </ul>
            <div className="market-paywall-modal__actions">
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                disabled={restoring}
                onClick={() => setPendingRestore(null)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--danger"
                disabled={restoring}
                onClick={() => void handleConfirmRestore()}
              >
                {restoring ? 'Откат…' : 'Да, откатить'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
